import React, {useState} from 'react';
import Grid from '@mui/material/Grid2';
import { Box, Button, Card, CardContent, CardMedia, FormControl, FormControlLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';

import { useErrorBoundary } from 'react-error-boundary';
import dayjs, { Dayjs } from 'dayjs';

import Timeline from '@mui/lab/Timeline';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';

import ElectionSchedules from '../../../types/municipal/electionSchedules';

const ElectionSchedulesCalculator = (): JSX.Element => {
  
  const { showBoundary } = useErrorBoundary();  
  const [hasSchedules, setHasSchedules] = useState<boolean>(false);
  const [schedules, setSchedules] = useState<ElectionSchedules>();
  const [isPreliminaryElection, setIsPreliminaryElection] = useState<boolean>(true);
  const [isGeneralElection, setIsGeneralElection] = useState<boolean>(false);
  const [primaryElectionDate, setPrimaryElectionDate] = useState<Dayjs | null>(dayjs(new Date()));
  const [generalElectionDate, setGeneralElectionDate] = useState<Dayjs | null>(dayjs(new Date()));

  const updatePreliminaryElection = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsPreliminaryElection((event.target as HTMLInputElement).value === 'true');
  };

  const updateGeneralEelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsGeneralElection((event.target as HTMLInputElement).value === 'true');
  };

  const getElectionSchedules = () => {

    try{
      let scheduleYear = primaryElectionDate?.year();
      let params = [
        `primaryElectionDate=${primaryElectionDate?.format('YYYY/MM/DD')}`,
        `generalElectionDate=${generalElectionDate?.format('YYYY/MM/DD')}`,
        `year=${scheduleYear}`,
        `hasPreliminary=${isPreliminaryElection}`,
        `isCityFallElection=${isGeneralElection}`,
      ].join('&');

      fetch(`${process.env.REACT_APP_API_BASE_URL}/municipality/calculator?${params}`)
      .then(result => {
        if (!result.ok) {
          showBoundary(new Error('Failed to get the municipal election schedules.'));
        }
        return result.json();
      })
      .then(result => {
        setSchedules(result);
        setHasSchedules(true);
      });
    }catch(error){
        console.error('Uncaught Error:', error);
    }
  }


  return (
    <Box marginTop={5} marginBottom={4}>
    <Box marginBottom={4}>
      <Typography
       variant="h6"
        sx={{
          textTransform: 'uppercase',
        }}
        fontWeight={700}
        gutterBottom
        color={'secondary'}
        align={'center'}
      >
        Election Schedule Calculator
      </Typography>
    </Box>
    <Grid container spacing={{ xs: 4, md: 8 }}>
        <Grid container size={{xs:12, md: 5}} alignItems={'center'}>
            <Box
                padding={3}
                width={1}
                height={1}
                component={Card}
                boxShadow={1}
                marginBottom={4}
            >
                <Grid container spacing={1}>
                    <Grid size={12}>
                        <Typography
                          variant={'subtitle1'}
                          fontWeight={650}
                          color="primary"
                          >
                          To calculate due dates and reporting periods, please answer the following questions:
                        </Typography>
                    </Grid>
                    <Grid size={12}>
                      <Typography
                        variant={'subtitle2'}
                        fontWeight={650}
                        >
                        Is there a preliminary election?
                        </Typography>
                      <FormControl>
                        <RadioGroup row name="preliminary-election" value={isPreliminaryElection} onChange={updatePreliminaryElection}>
                          <FormControlLabel value="true" control={<Radio />} label="Yes" />
                          <FormControlLabel value="false" control={<Radio />} label="No" />
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                    <Grid size={12}>
                      <Typography
                        variant={'subtitle2'}
                        fontWeight={650}
                        >
                        Is this a November election?
                        </Typography>
                      <FormControl>
                        <RadioGroup row name="general-election" value={isGeneralElection} onChange={updateGeneralEelection}>
                          <FormControlLabel value="true" control={<Radio />} label="Yes" />
                          <FormControlLabel value="false" control={<Radio />} label="No" />
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                    {
                      isPreliminaryElection &&
                      <Grid size={12}>
                        <Typography
                          variant={'subtitle2'}
                          fontWeight={650}
                        >
                        Preliminary Election Date
                        </Typography>
                        <FormControl size="small" sx={{ m: 1, p:0, minWidth: 120 }}>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DemoContainer components={['DatePicker']}>
                          <DatePicker
                            sx={{ fontSize: 'smaller' }} 
                            slotProps={{ textField: { size: 'small' } }}
                            value={primaryElectionDate}
                            onChange={(newValue) => setPrimaryElectionDate(newValue)}
                          />
                          </DemoContainer>
                          </LocalizationProvider>
                        </FormControl>
                      </Grid>
                    }
                  
                    <Grid size={12}>
                        <Typography
                        variant={'subtitle2'}
                        fontWeight={650}
                        >
                        Election (General) Date
                        </Typography>
                        <FormControl size="small" sx={{ m: 1, p:0, minWidth: 120 }}>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DemoContainer components={['DatePicker']}>
                          <DatePicker
                            sx={{ fontSize: 'smaller' }} 
                            slotProps={{ textField: { size: 'small' } }}
                            value={generalElectionDate}
                            onChange={(newValue) => setGeneralElectionDate(newValue)}
                          />
                          </DemoContainer>
                          </LocalizationProvider>
                        </FormControl>
                    </Grid>
                    <Box marginTop={3} display={'flex'} justifyContent={'left'}>
                        <Button
                          variant="contained"
                          color="primary"
                          size="large"
                          endIcon={<ArrowRightAltIcon />}
                          onClick={getElectionSchedules}
                        >
                        Get the Schedule
                      </Button>
                    </Box>
                </Grid>
            </Box>
        </Grid>
        <Grid container size={{xs:12, md:7}} alignItems={'center'} >
          <Box
            padding={{ xs: 3, sm: 6 }}
            width={1}
            height={1}
            component={Card}
            boxShadow={1}
            marginBottom={4}
            >
              {
                !hasSchedules &&
                <>
                    <CardMedia
                      sx={{
                      position: 'relative',
                      height: { xs: 240, md: 350 },
                      overflow: 'hidden',
                      }}
                    >
                      <iframe 
                        height={"100%"}
                        width={"100%"}
                        title='Local Election Schedule Calculator'
                        aria-label='Local Election Schedule Calculator'
                        src={'https://www.youtube.com/embed/a3QV05FIVUI?si=5navk3o3RuV9eSlX'} 
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share">
                      </iframe>
                    </CardMedia>
                    <CardContent>
                    <Typography
                      variant={'subtitle1'}
                      gutterBottom
                      align={'left'}
                      color='primary'
                      sx={{ fontWeight: 550 }}
                    >
                      Demo Video: How to Use OCPF Election Schedule Calculator
                    </Typography>
                  </CardContent>
                </> 
              }
              {
                hasSchedules &&
                <>
                    <Timeline
                    sx={{
                        [`& .${timelineItemClasses.root}:before`]: {
                          flex: 0,
                          padding: 0,
                      },
                      marginTop: -1,
                    }}
                    >
                    {
                      isPreliminaryElection &&
                        <TimelineItem>
                            <TimelineSeparator>
                            <TimelineDot color={'primary'} />
                            <TimelineConnector />
                            </TimelineSeparator>
                            <TimelineContent>
                                    <Stack spacing={1}>
                                        <Typography variant="subtitle2">
                                            Pre-Preliminary Election Period: <b>{schedules?.primaryElectionDate}</b> Primary Election
                                        </Typography>
                                        <Typography variant="subtitle2" color={'text.secondary'} component={"i"}>
                                            Reporting Period Start: {schedules?.prePrimaryStartDate}
                                        </Typography>
                                        <Typography variant="subtitle2" color={'text.secondary'} component={"i"}>
                                            Reporting Period End: {schedules?.prePrimaryEndDate}
                                        </Typography>
                                        <Typography variant="subtitle2" color={'text.secondary'} component={"i"}>
                                            Due Day: {schedules?.prePrimaryDueDate} 
                                        </Typography>
                                        {
                                            schedules?.prePrimaryNote && schedules.prePrimaryNote.length > 0 &&
                                            <Typography variant="subtitle2" color={'secondary'} component={"i"}>
                                             Note: {schedules?.prePrimaryNote} 
                                          </Typography>
                                        }
                                    </Stack>
                            </TimelineContent>
                        </TimelineItem>
                    }
                     <TimelineItem>
                            <TimelineSeparator>
                            <TimelineDot color={'primary'} />
                            <TimelineConnector />
                            </TimelineSeparator>
                            <TimelineContent>
                                    <Stack spacing={1}>
                                        <Typography variant="subtitle2">
                                            Pre-Election (General) Period: <b>{schedules?.generalElectionDate}</b> General Election
                                        </Typography>
                                        <Typography variant="subtitle2" color={'text.secondary'} component={"i"}>
                                            Reporting Period Start: {schedules?.preElectionStartDate}
                                        </Typography>
                                        <Typography variant="subtitle2" color={'text.secondary'} component={"i"}>
                                            Reporting Period End: {schedules?.preElectionEndDate}
                                        </Typography>
                                        <Typography variant="subtitle2" color={'text.secondary'} component={"i"}>
                                            Due Day: {schedules?.preElectionDueDate} 
                                        </Typography>
                                        {
                                            schedules?.preElectionNote && schedules.preElectionNote.length > 0 &&
                                            <Typography variant="subtitle2" color={'secondary'} component={"i"}>
                                             Note: {schedules?.preElectionNote} 
                                          </Typography>
                                        }
                                    </Stack>
                            </TimelineContent>
                    </TimelineItem>
                   {
                     isGeneralElection &&
                        <TimelineItem>
                            <TimelineSeparator>
                            <TimelineDot color={'primary'} />
                            <TimelineConnector />
                            </TimelineSeparator>
                            <TimelineContent>
                                    <Stack spacing={1}>
                                        <Typography variant="subtitle2">
                                          Year-End Period: <b>{schedules?.generalElectionDate}</b> general Election
                                        </Typography>
                                        <Typography variant="subtitle2" color={'text.secondary'} component={"i"}>
                                            Reporting Period Start: {schedules?.yeStartDate}
                                        </Typography>
                                        <Typography variant="subtitle2" color={'text.secondary'} component={"i"}>
                                            Reporting Period End: {schedules?.yeEndDate}
                                        </Typography>
                                        <Typography variant="subtitle2" color={'text.secondary'} component={"i"}>
                                            Due Day: {schedules?.yeDueDate} 
                                        </Typography>
                                        {
                                            schedules?.yeNote && schedules.yeNote.length > 0 &&
                                            <Typography variant="subtitle2" color={'secondary'} component={"i"}>
                                             Note: {schedules?.yeNote} 
                                          </Typography>
                                        }
                                    </Stack>
                            </TimelineContent>
                        </TimelineItem>
                   }
                   {
                     !isGeneralElection &&
                        <TimelineItem>
                            <TimelineSeparator>
                            <TimelineDot color={'primary'} />
                            <TimelineConnector />
                            </TimelineSeparator>
                            <TimelineContent>
                                    <Stack spacing={1}>
                                        <Typography variant="subtitle2">
                                          Post Election Period: <b>{schedules?.generalElectionDate}</b> general Election
                                        </Typography>
                                        <Typography variant="subtitle2" color={'text.secondary'} component={"i"}>
                                            Reporting Period Start: {schedules?.postElectionStartDate}
                                        </Typography>
                                        <Typography variant="subtitle2" color={'text.secondary'} component={"i"}>
                                            Reporting Period End: {schedules?.postElectionEndDate}
                                        </Typography>
                                        <Typography variant="subtitle2" color={'text.secondary'} component={"i"}>
                                            Due Day: {schedules?.postElectionDueDate} 
                                        </Typography>
                                        {
                                            schedules?.postElectionNote && schedules.postElectionNote.length > 0 &&
                                            <Typography variant="subtitle2" color={'secondary'} component={"i"}>
                                             Note: {schedules?.postElectionNote} 
                                          </Typography>
                                        }
                                    </Stack>
                            </TimelineContent>
                        </TimelineItem>
                   }
                   </Timeline>
                   <Box marginTop={1} display={'flex'} justifyContent={'right'}>
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={setHasSchedules.bind(this, false)}
                        >
                        Watch the video again
                      </Button>
                    </Box>          
                </>
              }
          </Box>
        </Grid>
    </Grid>
  </Box>
   
  );
};

export default ElectionSchedulesCalculator;
